import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
    Collapse,
} from 'react-bootstrap';

import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import SEO from '@/components/common/SEO';
import {
    getCommunities,
    getPendingCommunities,
    approveCommunity,
    rejectCommunity,
    deleteCommunity,
    setNeedsModerators,
    getNeedsModeratorsRequests,
    decideNeedsModerators,
} from '@/shared/services/communityApi';
import {
    getMemberships,
    updateMemberRole,
} from '@/shared/services/membershipApi';

import RequestReviewCard from './components/RequestReviewCard';
import './admin-ui.css';
import './CommunitiesAdmin.css';

/**
 * Format an ISO date string for the admin queue and management cards.
 * @param {string} dateStr - ISO 8601 date string
 * @returns {string} - Localized date/time, or an em-dash if dateStr is empty
 */
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

/**
 * Resolve a human-readable name for a community's requester. Server-side
 * populate is the expected path; the other branches are defensive fallbacks
 * for the rare case where a community arrives unpopulated.
 * @param {object|string|null|undefined} requestedBy - Populated user, raw id string, or empty
 * @returns {string} - Display name (username, full name, email) or "Unknown requester"
 */
const requesterDisplayName = (requestedBy) => {
    if (!requestedBy) return 'Unknown requester';
    // A raw id string is unhelpful as a display name — fall back to the
    // generic label so we don't surface ObjectIds to admins.
    if (typeof requestedBy === 'string') return 'Unknown requester';
    if (requestedBy.username) return requestedBy.username;
    if (requestedBy.name) {
        const { first, last } = requestedBy.name;
        return [first, last].filter(Boolean).join(' ') || 'Unknown requester';
    }
    if (requestedBy.email) return requestedBy.email;
    return 'Unknown requester';
};

/**
 * Resolve a display name for a CommunityMembership document, used for
 * moderator chips and the manage-moderators modal rows.
 * @param {object} membership - Membership returned from the memberships API
 * @returns {string} - Username when populated, otherwise a generic fallback
 */
const moderatorDisplayName = (membership) => {
    const u = membership.user || {};
    return u.username || 'Unknown user';
};

export default function CommunitiesAdmin() {
    const api = useAuthenticatedAxios();

    const [pending, setPending] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [pendingError, setPendingError] = useState(null);

    const [modRequests, setModRequests] = useState([]);
    const [modRequestsLoading, setModRequestsLoading] = useState(true);
    const [modRequestsError, setModRequestsError] = useState(null);

    const [active, setActive] = useState([]);
    // Map of communityId → array of moderator user summaries
    // ({username, profilePicture}). Populated from the community.moderators
    // expansion on /api/communities?include=moderators and kept in sync via
    // the manage-moderators modal toggle. Both writers deliberately omit
    // User._id (which IS the Firebase UID) — chips key on `username`, which
    // is unique platform-wide.
    const [moderatorsByCommunity, setModeratorsByCommunity] = useState({});
    const [activeLoading, setActiveLoading] = useState(true);
    const [activeError, setActiveError] = useState(null);

    // Per-community spinner state for approve / reject / archive in flight
    const [actionInProgress, setActionInProgress] = useState(null);

    // ID of the pending request whose guidelines section is currently expanded
    const [expandedGuidelinesId, setExpandedGuidelinesId] = useState(null);

    // Decision modal — handles approve, reject, archive
    const [decisionModal, setDecisionModal] = useState({
        show: false,
        community: null,
        action: null, // 'approve' | 'reject' | 'archive'
        rejectionReason: '',
        rejectionError: null,
    });

    // Manage moderators modal
    const [manageModsModal, setManageModsModal] = useState({
        show: false,
        community: null,
        members: [],
        loading: false,
        error: null,
    });
    const [memberActionInProgress, setMemberActionInProgress] = useState(null);
    // True while a needsModerators toggle request is in flight, so the
    // open/close applications button can disable itself and avoid double-fires.
    const [applicationsToggleInProgress, setApplicationsToggleInProgress] =
        useState(false);

    const fetchPending = useCallback(async () => {
        try {
            setPendingLoading(true);
            setPendingError(null);
            const data = await getPendingCommunities(api);
            setPending(data);
        } catch (err) {
            console.error('Failed to fetch pending communities:', err);
            setPendingError(
                err.response?.data?.message ||
                    'Failed to load pending requests.'
            );
        } finally {
            setPendingLoading(false);
        }
    }, [api]);

    const fetchModRequests = useCallback(async () => {
        try {
            setModRequestsLoading(true);
            setModRequestsError(null);
            const data = await getNeedsModeratorsRequests(api);
            setModRequests(data);
        } catch (err) {
            console.error(
                'Failed to fetch pending moderator recruitment requests:',
                err
            );
            setModRequestsError(
                err.response?.data?.message ||
                    'Failed to fetch pending moderator recruitment requests.'
            );
        } finally {
            setModRequestsLoading(false);
        }
    }, [api]);

    const fetchActive = useCallback(async () => {
        try {
            setActiveLoading(true);
            setActiveError(null);
            // Single round-trip with the include=moderators expansion (admin-
            // gated server-side, see community.controller.index). Previously
            // this was a getCommunities() + per-community getMemberships()
            // N+1; the server now returns the moderator list inline.
            const communities = await getCommunities(
                { include: 'moderators' },
                api
            );
            setActive(communities);

            const map = {};
            communities.forEach((c) => {
                map[c._id] = c.moderators || [];
            });
            setModeratorsByCommunity(map);
        } catch (err) {
            console.error('Failed to fetch active communities:', err);
            setActiveError(
                err.response?.data?.message ||
                    'Failed to load active communities.'
            );
        } finally {
            setActiveLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchPending();
        fetchModRequests();
        fetchActive();
    }, [fetchPending, fetchModRequests, fetchActive]);

    const openDecisionModal = (community, action) => {
        setDecisionModal({
            show: true,
            community,
            action,
            rejectionReason: '',
            rejectionError: null,
        });
    };

    const closeDecisionModal = () => {
        setDecisionModal({
            show: false,
            community: null,
            action: null,
            rejectionReason: '',
            rejectionError: null,
        });
    };

    const submitDecision = async () => {
        const { community, action, rejectionReason } = decisionModal;
        if (!community || !action) return;

        if (action === 'reject' && !rejectionReason.trim()) {
            setDecisionModal((prev) => ({
                ...prev,
                rejectionError: 'A rejection reason is required.',
            }));
            return;
        }

        setActionInProgress(community._id);
        try {
            if (action === 'approve') {
                await approveCommunity(api, community._id);
                setPending((prev) =>
                    prev.filter((c) => c._id !== community._id)
                );
                // Approved communities are now active — refetch to surface them.
                fetchActive();
            } else if (action === 'reject') {
                await rejectCommunity(
                    api,
                    community._id,
                    rejectionReason.trim()
                );
                setPending((prev) =>
                    prev.filter((c) => c._id !== community._id)
                );
            } else if (action === 'archive') {
                await deleteCommunity(api, community._id);
                setActive((prev) =>
                    prev.filter((c) => c._id !== community._id)
                );
                setModeratorsByCommunity((prev) => {
                    const next = { ...prev };
                    delete next[community._id];
                    return next;
                });
            } else if (action === 'openModApps') {
                const data = await decideNeedsModerators(
                    api,
                    community._id,
                    'approve'
                );
                setModRequests((prev) =>
                    prev.filter((c) => c._id !== community._id)
                );
                setActive((prev) =>
                    prev.map((c) => (c._id === data._id ? data : c))
                );
            } else if (action === 'denyModApps') {
                const data = await decideNeedsModerators(
                    api,
                    community._id,
                    'deny'
                );
                setModRequests((prev) =>
                    prev.filter((c) => c._id !== community._id)
                );
                setActive((prev) =>
                    prev.map((c) => (c._id === data._id ? data : c))
                );
            }
            closeDecisionModal();
        } catch (err) {
            console.error('Decision action failed:', err);
            setDecisionModal((prev) => ({
                ...prev,
                rejectionError:
                    err.response?.data?.message ||
                    `Failed to ${action} community.`,
            }));
        } finally {
            setActionInProgress(null);
        }
    };

    const openManageMods = async (community) => {
        setManageModsModal({
            show: true,
            community,
            members: [],
            loading: true,
            error: null,
        });
        try {
            // limit=100 matches the maximum any single response can carry and
            // keeps the modal's member list aligned with the moderator chips
            // on the underlying card. Without it, getMemberships defaults to
            // limit=10 sorted by newest membership — a community with >10
            // members where moderators sit outside the newest 10 would render
            // chips correctly but leave those moderators unmanageable in the
            // modal. A future ticket can paginate explicitly if rosters grow
            // past this cap.
            const members = await getMemberships({
                communityId: community._id,
                status: 'active',
                limit: 100,
            });
            setManageModsModal((prev) => ({
                ...prev,
                members,
                loading: false,
            }));
        } catch (err) {
            console.error('Failed to load members:', err);
            setManageModsModal((prev) => ({
                ...prev,
                loading: false,
                error: err.response?.data?.message || 'Failed to load members.',
            }));
        }
    };

    const closeManageMods = () => {
        setManageModsModal({
            show: false,
            community: null,
            members: [],
            loading: false,
            error: null,
        });
    };

    const toggleAllowModApplications = async (allowApps, communityId) => {
        setApplicationsToggleInProgress(true);
        try {
            const data = await setNeedsModerators(api, communityId, allowApps);
            setManageModsModal((prev) =>
                prev.community && prev.community._id === data._id
                    ? { ...prev, community: data }
                    : prev
            );
            setActive((prev) =>
                prev.map((c) => (c._id === data._id ? data : c))
            );
            // Enabling recruitment clears needsModeratorsRequested server-side,
            // so the community no longer belongs in the recruitment-requests
            // queue. Reconcile from the returned doc: if the request flag is
            // now false, drop it from modRequests (a no-op if it wasn't there).
            if (data.needsModeratorsRequested === false) {
                setModRequests((prev) =>
                    prev.filter((c) => c._id !== data._id)
                );
            }
        } catch (err) {
            console.error('Failed to update moderator applications:', err);
            setManageModsModal((prev) => ({
                ...prev,
                error:
                    err.response?.data?.message ||
                    'Failed to update moderator applications.',
            }));
        } finally {
            setApplicationsToggleInProgress(false);
        }
    };

    const toggleMemberRole = async (membership) => {
        // Capture the community id up front so the post-await update path
        // doesn't depend on the modal still being open with the same community.
        const communityId = manageModsModal.community?._id;
        if (!communityId) return;

        const newRole =
            membership.role === 'moderator' ? 'member' : 'moderator';
        setMemberActionInProgress(membership._id);
        try {
            const updated = await updateMemberRole(
                api,
                membership._id,
                newRole
            );
            // Update the row in the modal's local list
            setManageModsModal((prev) => ({
                ...prev,
                members: prev.members.map((m) =>
                    m._id === membership._id ? { ...m, role: updated.role } : m
                ),
            }));
            // Refresh moderator chips on the underlying card. Both writers
            // to moderatorsByCommunity (the initial community.moderators
            // server expansion and this optimistic add) ship the same
            // shape: {_id, username, profilePicture}. The User schema's
            // `name` field is a nested {first, last} subdocument and is
            // deliberately omitted from both projections — the chip render
            // only reads `username` today; a future ticket can add a
            // flattened displayName server-side if chips need a person's
            // real name.
            const user = membership.user;
            if (user?.username) {
                setModeratorsByCommunity((prev) => {
                    const otherMods = (prev[communityId] || []).filter(
                        (u) => u.username !== user.username
                    );
                    if (newRole === 'moderator') {
                        return {
                            ...prev,
                            [communityId]: [
                                ...otherMods,
                                {
                                    username: user.username,
                                    profilePicture: user.profilePicture,
                                },
                            ],
                        };
                    }
                    return { ...prev, [communityId]: otherMods };
                });
            }
        } catch (err) {
            console.error('Failed to update member role:', err);
            setManageModsModal((prev) => ({
                ...prev,
                error:
                    err.response?.data?.message ||
                    'Failed to update member role.',
            }));
        } finally {
            setMemberActionInProgress(null);
        }
    };

    const decisionConfig = (() => {
        if (!decisionModal.action) return null;
        if (decisionModal.action === 'approve') {
            return {
                tone: 'approve',
                icon: 'bi-check2-circle',
                eyebrow: 'Community request decision',
                title: 'Approve this community?',
                message:
                    'Approving makes the community active immediately and assigns the requester as its first moderator.',
                actionLabel: 'Approve and Publish',
                buttonVariant: 'success',
                requestLabel: 'Request',
                consequences: [
                    'Community becomes visible to all users',
                    'Requester becomes the first moderator',
                ],
            };
        }
        if (decisionModal.action === 'reject') {
            return {
                tone: 'reject',
                icon: 'bi-x-octagon',
                eyebrow: 'Community request decision',
                title: 'Reject this community?',
                message:
                    'Rejecting marks the request as denied. The reason you provide will be recorded and shared with the requester.',
                actionLabel: 'Reject Request',
                buttonVariant: 'danger',
                requestLabel: 'Request',
                consequences: [
                    'Community will not be created',
                    'Requester sees the rejection reason',
                ],
            };
        }
        if (decisionModal.action === 'openModApps') {
            return {
                tone: 'approve',
                icon: 'bi-check2-circle',
                eyebrow: 'Moderator recruitment request decision',
                title: 'Approve this request?',
                message:
                    'Approving allows members of this community to apply for the Moderator role.',
                actionLabel: 'Approve Request',
                buttonVariant: 'success',
                requestLabel: 'Request',
                consequences: [
                    'Moderator applications will be opened for this community',
                ],
            };
        }
        if (decisionModal.action === 'denyModApps') {
            return {
                tone: 'reject',
                icon: 'bi-x-octagon',
                eyebrow: 'Moderator recruitment request decision',
                title: 'Deny this request?',
                message:
                    'Denying means members of this community will not be allowed to apply for the Moderator role',
                actionLabel: 'Reject Request',
                buttonVariant: 'danger',
                requestLabel: 'Request',
                consequences: ['Moderator applications will not be opened'],
            };
        }
        // archive
        return {
            tone: 'reject',
            icon: 'bi-archive',
            eyebrow: 'Community management',
            title: 'Archive this community?',
            message:
                'Archiving hides the community from the public and prevents new posts and members.',
            actionLabel: 'Archive Community',
            buttonVariant: 'danger',
            requestLabel: 'Community',
            consequences: [
                'Hidden from public lists and detail pages',
                'No new posts, replies, or members',
            ],
        };
    })();

    const isReject = decisionModal.action === 'reject';

    return (
        <div className="py-4">
            <SEO title="Admin: Communities" />
            <Container>
                <h1 className="fs-3 fw-bold text-white mb-4">Communities</h1>

                {/* Community Creation Requests section */}
                <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h2 className="fs-5 fw-semibold text-white mb-0">
                            Community Creation Requests
                        </h2>
                        {pending.length > 0 && (
                            <Badge bg="warning" text="dark" pill>
                                {pending.length}
                            </Badge>
                        )}
                    </div>
                    <p className="text-white small mb-3">
                        Community creation requests awaiting site admin review.
                        Approve to publish, or reject with a reason.
                    </p>

                    {pendingLoading && (
                        <div className="d-flex justify-content-center p-4">
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                            />
                            <span className="visually-hidden">
                                Loading pending requests...
                            </span>
                        </div>
                    )}

                    {!pendingLoading && pendingError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setPendingError(null)}
                        >
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p className="mb-2">{pendingError}</p>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={fetchPending}
                            >
                                Try Again
                            </Button>
                        </Alert>
                    )}

                    {!pendingLoading &&
                        !pendingError &&
                        pending.length === 0 && (
                            <Alert variant="success">
                                No pending community requests. Queue is clear!
                            </Alert>
                        )}

                    {!pendingLoading && !pendingError && pending.length > 0 && (
                        <div className="d-grid gap-3">
                            {pending.map((community) => {
                                const isBusy =
                                    actionInProgress === community._id;
                                return (
                                    <RequestReviewCard
                                        key={community._id}
                                        community={community}
                                        isBusy={isBusy}
                                        onApprove={() =>
                                            openDecisionModal(
                                                community,
                                                'approve'
                                            )
                                        }
                                        onReject={() =>
                                            openDecisionModal(
                                                community,
                                                'reject'
                                            )
                                        }
                                        approveLabel={`Approve ${community.name}`}
                                        rejectLabel={`Reject ${community.name}`}
                                    >
                                        <div className="pending-review-identity">
                                            {community.icon && (
                                                <img
                                                    src={community.icon}
                                                    alt=""
                                                    className="pending-review-icon"
                                                />
                                            )}
                                            <h3 className="pending-review-title fw-bold mb-0">
                                                {community.name}
                                            </h3>
                                        </div>

                                        <div className="pending-review-meta d-flex flex-wrap align-items-center gap-2">
                                            <span>
                                                <i
                                                    className="bi bi-person-circle"
                                                    aria-hidden="true"
                                                />
                                                {requesterDisplayName(
                                                    community.requestedBy
                                                )}
                                            </span>
                                            {community.createdAt && (
                                                <span>
                                                    <i
                                                        className="bi bi-calendar3"
                                                        aria-hidden="true"
                                                    />
                                                    {formatDate(
                                                        community.createdAt
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        <div className="pending-review-community d-flex flex-wrap align-items-center gap-2">
                                            <span className="pending-review-community-name d-inline-flex align-items-center fw-semibold">
                                                <i
                                                    className="bi bi-link-45deg"
                                                    aria-hidden="true"
                                                />
                                                /communities/
                                                {community.slug}
                                            </span>
                                        </div>

                                        <div className="pending-review-content border rounded-3 bg-body-tertiary">
                                            {community.description || (
                                                <span className="text-body-secondary">
                                                    No description provided.
                                                </span>
                                            )}
                                        </div>

                                        {community.tags &&
                                            community.tags.length > 0 && (
                                                <div className="pending-review-tags">
                                                    {community.tags.map(
                                                        (tag) => (
                                                            <span
                                                                key={tag}
                                                                className="pending-review-tag"
                                                            >
                                                                <i
                                                                    className="bi bi-hash"
                                                                    aria-hidden="true"
                                                                />
                                                                {tag}
                                                            </span>
                                                        )
                                                    )}
                                                </div>
                                            )}

                                        {community.guidelines && (
                                            <div className="pending-review-guidelines">
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="pending-review-guidelines-toggle p-0"
                                                    onClick={() =>
                                                        setExpandedGuidelinesId(
                                                            expandedGuidelinesId ===
                                                                community._id
                                                                ? null
                                                                : community._id
                                                        )
                                                    }
                                                    aria-expanded={
                                                        expandedGuidelinesId ===
                                                        community._id
                                                    }
                                                    aria-controls={`guidelines-${community._id}`}
                                                >
                                                    <i
                                                        className={`bi ${
                                                            expandedGuidelinesId ===
                                                            community._id
                                                                ? 'bi-chevron-up'
                                                                : 'bi-chevron-down'
                                                        } me-1`}
                                                        aria-hidden="true"
                                                    />
                                                    {expandedGuidelinesId ===
                                                    community._id
                                                        ? 'Hide guidelines'
                                                        : 'Show guidelines'}
                                                </Button>
                                                <Collapse
                                                    in={
                                                        expandedGuidelinesId ===
                                                        community._id
                                                    }
                                                >
                                                    <div
                                                        id={`guidelines-${community._id}`}
                                                    >
                                                        <div className="pending-review-guidelines-content">
                                                            {
                                                                community.guidelines
                                                            }
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </div>
                                        )}
                                    </RequestReviewCard>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Moderator Recruitment Requests section */}
                <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h2 className="fs-5 fw-semibold text-white mb-0">
                            Moderator Recruitment Requests
                        </h2>
                        {modRequests.length > 0 && (
                            <Badge bg="warning" text="dark" pill>
                                {modRequests.length}
                            </Badge>
                        )}
                    </div>
                    <p className="text-white small mb-3">
                        Moderator recruitment requests awaiting site admin
                        review. Approve or deny requests to open moderator
                        applications for requesting communities.
                    </p>

                    {modRequestsLoading && (
                        <div className="d-flex justify-content-center p-4">
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                            />
                            <span className="visually-hidden">
                                Loading moderator recruitment requests...
                            </span>
                        </div>
                    )}

                    {!modRequestsLoading && modRequestsError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setModRequestsError(null)}
                        >
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p className="mb-2">{modRequestsError}</p>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={fetchModRequests}
                            >
                                Try Again
                            </Button>
                        </Alert>
                    )}

                    {!modRequestsLoading &&
                        !modRequestsError &&
                        modRequests.length === 0 && (
                            <Alert variant="success">
                                No pending moderator recruitment requests. Queue
                                is clear!
                            </Alert>
                        )}

                    {!modRequestsLoading &&
                        !modRequestsError &&
                        modRequests.length > 0 && (
                            <div className="d-grid gap-3">
                                {modRequests.map((community) => {
                                    const isBusy =
                                        actionInProgress === community._id;
                                    return (
                                        <RequestReviewCard
                                            key={community._id}
                                            community={community}
                                            isBusy={isBusy}
                                            onApprove={() =>
                                                openDecisionModal(
                                                    community,
                                                    'openModApps'
                                                )
                                            }
                                            onReject={() =>
                                                openDecisionModal(
                                                    community,
                                                    'denyModApps'
                                                )
                                            }
                                            approveLabel={`Accept request from ${community.name}`}
                                            rejectLabel={`Deny request from ${community.name}`}
                                        >
                                            <div className="pending-review-identity">
                                                {community.icon && (
                                                    <img
                                                        src={community.icon}
                                                        alt=""
                                                        className="pending-review-icon"
                                                    />
                                                )}
                                                <h3 className="pending-review-title fw-bold mb-0">
                                                    {community.name} would like
                                                    to open Moderator
                                                    applications
                                                </h3>
                                            </div>

                                            <div className="pending-review-community d-flex flex-wrap align-items-center gap-2">
                                                <span className="pending-review-community-name d-inline-flex align-items-center fw-semibold">
                                                    <i
                                                        className="bi bi-link-45deg"
                                                        aria-hidden="true"
                                                    />
                                                    /communities/
                                                    {community.slug}
                                                </span>
                                            </div>
                                        </RequestReviewCard>
                                    );
                                })}
                            </div>
                        )}
                </div>

                {/* Active Communities section */}
                <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h2 className="fs-5 fw-semibold text-white mb-0">
                            Active Communities
                        </h2>
                        {active.length > 0 && (
                            <Badge bg="light" text="dark" pill>
                                {active.length}
                            </Badge>
                        )}
                    </div>
                    <p className="text-white small mb-3">
                        All published communities. Manage moderator assignments
                        or archive a community from this view.
                    </p>

                    {activeLoading && (
                        <div className="d-flex justify-content-center p-4">
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                            />
                            <span className="visually-hidden">
                                Loading active communities...
                            </span>
                        </div>
                    )}

                    {!activeLoading && activeError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setActiveError(null)}
                        >
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p className="mb-2">{activeError}</p>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={fetchActive}
                            >
                                Try Again
                            </Button>
                        </Alert>
                    )}

                    {!activeLoading && !activeError && active.length === 0 && (
                        <Alert variant="info">No active communities yet.</Alert>
                    )}

                    {!activeLoading && !activeError && active.length > 0 && (
                        <div className="d-grid gap-3">
                            {active.map((community) => {
                                const mods =
                                    moderatorsByCommunity[community._id] || [];
                                const isBusy =
                                    actionInProgress === community._id;
                                return (
                                    <Card
                                        key={community._id}
                                        className="active-community-card shadow-sm overflow-hidden rounded-3"
                                    >
                                        <Card.Body className="active-community-card-body d-grid">
                                            <div className="active-community-main">
                                                <h3 className="active-community-title fw-bold">
                                                    {community.name}
                                                </h3>
                                                <div className="active-community-meta d-flex flex-wrap align-items-center gap-2">
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        className="border fw-semibold"
                                                    >
                                                        {community.category}
                                                    </Badge>
                                                    <span>
                                                        <i
                                                            className="bi bi-people me-1"
                                                            aria-hidden="true"
                                                        />
                                                        {community.memberCount ||
                                                            0}{' '}
                                                        members
                                                    </span>
                                                    <span>
                                                        <i
                                                            className="bi bi-link-45deg me-1"
                                                            aria-hidden="true"
                                                        />
                                                        /communities/
                                                        {community.slug}
                                                    </span>
                                                </div>
                                                <div className="active-community-mods d-flex flex-wrap align-items-center">
                                                    <span className="active-community-mods-label fw-bold text-uppercase">
                                                        Moderators:
                                                    </span>
                                                    {mods.length === 0 && (
                                                        <span className="active-community-mod-chip-empty fst-italic">
                                                            None assigned
                                                        </span>
                                                    )}
                                                    {mods.map((u) => (
                                                        <span
                                                            key={u.username}
                                                            className="active-community-mod-chip d-inline-flex align-items-center border bg-body-tertiary rounded-pill fw-semibold"
                                                        >
                                                            <i
                                                                className="bi bi-person-badge"
                                                                aria-hidden="true"
                                                            />
                                                            {u.username ||
                                                                'Unknown user'}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="active-community-actions d-grid d-md-flex flex-md-column gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    disabled={isBusy}
                                                    onClick={() =>
                                                        openManageMods(
                                                            community
                                                        )
                                                    }
                                                    aria-label={`Manage moderators for ${community.name}`}
                                                >
                                                    <i
                                                        className="bi bi-people me-2"
                                                        aria-hidden="true"
                                                    />
                                                    Manage Moderators
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    disabled={isBusy}
                                                    onClick={() =>
                                                        openDecisionModal(
                                                            community,
                                                            'archive'
                                                        )
                                                    }
                                                    aria-label={`Archive ${community.name}`}
                                                >
                                                    <i
                                                        className="bi bi-archive me-2"
                                                        aria-hidden="true"
                                                    />
                                                    Archive
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Decision Modal — approve, reject, archive */}
                <Modal
                    show={decisionModal.show}
                    onHide={closeDecisionModal}
                    centered
                    aria-labelledby="community-decision-modal-title"
                    className="moderation-confirm-modal"
                >
                    {decisionConfig && decisionModal.community && (
                        <>
                            <Modal.Header closeButton className="border-0 pb-0">
                                <Modal.Title
                                    id="community-decision-modal-title"
                                    className="visually-hidden"
                                >
                                    {decisionConfig.title}
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="pt-3">
                                <div
                                    className={`moderation-confirm-hero moderation-confirm-hero-${decisionConfig.tone} d-grid`}
                                >
                                    <div className="moderation-confirm-icon d-inline-flex align-items-center justify-content-center rounded-circle text-white">
                                        <i
                                            className={`bi ${decisionConfig.icon}`}
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="moderation-confirm-copy">
                                        <p className="moderation-confirm-eyebrow fw-bold text-uppercase">
                                            {decisionConfig.eyebrow}
                                        </p>
                                        <h3>{decisionConfig.title}</h3>
                                        <p>{decisionConfig.message}</p>
                                    </div>
                                </div>

                                <div className="moderation-confirm-post d-grid border rounded-3 bg-body-tertiary gap-1">
                                    <span>{decisionConfig.requestLabel}</span>
                                    <strong>
                                        {decisionModal.action ===
                                            'openModApps' ||
                                        decisionModal.action ===
                                            'denyModApps' ? (
                                            <>
                                                {decisionModal.community.name}{' '}
                                                would like to open Moderator
                                                applications
                                            </>
                                        ) : (
                                            <>
                                                &ldquo;
                                                {decisionModal.community.name}
                                                &rdquo;
                                            </>
                                        )}
                                    </strong>
                                </div>

                                {isReject && (
                                    <div className="moderation-confirm-reason">
                                        <label
                                            htmlFor="reject-reason-input"
                                            className="moderation-confirm-reason-label"
                                        >
                                            Rejection reason{' '}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </label>
                                        <Form.Control
                                            id="reject-reason-input"
                                            as="textarea"
                                            rows={3}
                                            value={
                                                decisionModal.rejectionReason
                                            }
                                            onChange={(e) =>
                                                setDecisionModal((prev) => ({
                                                    ...prev,
                                                    rejectionReason:
                                                        e.target.value,
                                                    rejectionError: null,
                                                }))
                                            }
                                            placeholder="Explain why this community cannot be approved..."
                                            isInvalid={
                                                !!decisionModal.rejectionError
                                            }
                                        />
                                        {decisionModal.rejectionError && (
                                            <Form.Control.Feedback type="invalid">
                                                {decisionModal.rejectionError}
                                            </Form.Control.Feedback>
                                        )}
                                    </div>
                                )}

                                {!isReject && decisionModal.rejectionError && (
                                    <Alert variant="danger" className="mb-3">
                                        {decisionModal.rejectionError}
                                    </Alert>
                                )}

                                <div className="moderation-confirm-effects d-grid">
                                    {decisionConfig.consequences.map((item) => (
                                        <span key={item}>
                                            <i
                                                className="bi bi-dot"
                                                aria-hidden="true"
                                            />
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </Modal.Body>
                            <Modal.Footer className="border-0 pt-0 moderation-confirm-footer d-grid">
                                <Button
                                    variant="outline-primary"
                                    onClick={closeDecisionModal}
                                    disabled={
                                        actionInProgress ===
                                        decisionModal.community._id
                                    }
                                >
                                    Keep Reviewing
                                </Button>
                                <Button
                                    variant={decisionConfig.buttonVariant}
                                    onClick={submitDecision}
                                    disabled={
                                        actionInProgress ===
                                        decisionModal.community._id
                                    }
                                >
                                    {actionInProgress ===
                                    decisionModal.community._id ? (
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                    ) : (
                                        <i
                                            className={`bi ${decisionConfig.icon} me-2`}
                                            aria-hidden="true"
                                        />
                                    )}
                                    {decisionConfig.actionLabel}
                                </Button>
                            </Modal.Footer>
                        </>
                    )}
                </Modal>

                {/* Manage Moderators Modal */}
                <Modal
                    show={manageModsModal.show}
                    onHide={closeManageMods}
                    centered
                    size="lg"
                    aria-labelledby="manage-mods-modal-title"
                >
                    <Modal.Header closeButton>
                        <Modal.Title
                            id="manage-mods-modal-title"
                            className="fs-5"
                        >
                            Manage Moderators
                            {manageModsModal.community && (
                                <span className="text-success fs-6 ms-2">
                                    — {manageModsModal.community.name}
                                </span>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-success small mb-3">
                            Promote any active member to moderator, or demote an
                            existing moderator back to member. Banned members
                            are not shown.
                        </p>

                        {manageModsModal.error && (
                            <Alert
                                variant="danger"
                                dismissible
                                onClose={() =>
                                    setManageModsModal((prev) => ({
                                        ...prev,
                                        error: null,
                                    }))
                                }
                            >
                                {manageModsModal.error}
                            </Alert>
                        )}

                        {manageModsModal.loading && (
                            <div className="d-flex justify-content-center p-4">
                                <Spinner animation="border" size="sm" />
                                <span className="visually-hidden">
                                    Loading members...
                                </span>
                            </div>
                        )}

                        {!manageModsModal.loading &&
                            !manageModsModal.error &&
                            manageModsModal.members.length === 0 && (
                                <Alert variant="info" className="mb-0">
                                    No active members in this community yet.
                                </Alert>
                            )}

                        {!manageModsModal.loading &&
                            manageModsModal.members.length > 0 && (
                                <div className="manage-mods-list d-grid gap-2">
                                    {manageModsModal.members.map((m) => {
                                        const isMod = m.role === 'moderator';
                                        const isBusy =
                                            memberActionInProgress === m._id;
                                        return (
                                            <div
                                                key={m._id}
                                                className="manage-mods-row d-grid align-items-center border rounded"
                                            >
                                                <div>
                                                    <div className="manage-mods-row-name fw-semibold">
                                                        {moderatorDisplayName(
                                                            m
                                                        )}
                                                    </div>
                                                    <div className="manage-mods-row-meta">
                                                        {isMod
                                                            ? 'Moderator'
                                                            : 'Member'}
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant={
                                                        isMod
                                                            ? 'outline-success'
                                                            : 'outline-primary'
                                                    }
                                                    disabled={isBusy}
                                                    onClick={() =>
                                                        toggleMemberRole(m)
                                                    }
                                                >
                                                    {isBusy ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                            className="me-2"
                                                        />
                                                    ) : null}
                                                    {isMod
                                                        ? 'Demote to member'
                                                        : 'Promote to moderator'}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                    </Modal.Body>
                    <Modal.Footer>
                        {manageModsModal.community &&
                            (manageModsModal.community.needsModerators ? (
                                <Button
                                    variant="outline-primary"
                                    disabled={applicationsToggleInProgress}
                                    onClick={() =>
                                        toggleAllowModApplications(
                                            false,
                                            manageModsModal.community._id
                                        )
                                    }
                                >
                                    {applicationsToggleInProgress ? (
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                    ) : null}
                                    Close Moderator Applications
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    disabled={applicationsToggleInProgress}
                                    onClick={() =>
                                        toggleAllowModApplications(
                                            true,
                                            manageModsModal.community._id
                                        )
                                    }
                                >
                                    {applicationsToggleInProgress ? (
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                    ) : null}
                                    Open Moderator Applications
                                </Button>
                            ))}
                        <Button
                            variant="outline-primary"
                            onClick={closeManageMods}
                        >
                            Done
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
}
